/**
 * HelloRetailSearchOverlay
 *
 * Handles the predictive search overlay in the header.
 * Pure UI module — no helpers, no HTML builders defined here.
 * All rendering utilities live in hello-retail-helpers.js (window.HelloRetailHelpers).
 *
 * Lego-block architecture:
 *   hello-retail-helpers.js        → shared rendering utilities + CARD_SLOT_FILLERS
 *   hello-retail-api.js            → data layer (Hello Retail SDK wrapper)
 *   hello-retail-search.js         → this file: overlay behaviour
 *   hello-retail-templates.liquid  → view layer (HTML structure, Liquid at render time)
 *
 * Load order (all defer in theme.liquid):
 *   1. hello-retail-helpers.js
 *   2. hello-retail-api.js
 *   3. hello-retail-search.js
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // HelloRetailSearchOverlay
  // ---------------------------------------------------------------------------

  function HelloRetailSearchOverlay() {
    this.api            = new window.HelloRetailAPI();
    this.inputEl        = document.querySelector('#predictive-search-form input[name="q"]');
    this.resultsEl      = document.querySelector('#header-predictive-search .hr-search-results');
    this._debounceTimer = null;
    // Incremented on every new search so stale loadRecom responses are discarded
    this._recomGen      = 0;
    /** Last HR query that produced the visible product grid (used so submit can send q=* for suggested products). */
    this._lastSuccessfulSearchQuery = null;
  }

  HelloRetailSearchOverlay.prototype._syncPlaceholder = function () {
    var mobile  = this.inputEl.dataset.placeholderMobile;
    var desktop = this.inputEl.dataset.placeholderDesktop;
    if (!mobile && !desktop) return;
    this.inputEl.placeholder = window.matchMedia('(min-width: 1000px)').matches
      ? (desktop || mobile)
      : (mobile  || desktop);
  };

  HelloRetailSearchOverlay.prototype.init = function () {
    if (!this.inputEl || !this.resultsEl) return;

    this._syncPlaceholder();
    window.addEventListener('resize', this._syncPlaceholder.bind(this));

    this.inputEl.addEventListener('input', this._onInput.bind(this));

    var form = this.inputEl.form;
    if (form) {
      form.addEventListener('submit', this._onPredictiveFormSubmit.bind(this));
    }

    // Attach dialog open/close listeners directly on the <header-search> element
    // (events are dispatched on the element itself, no bubbling)
    var dialogEl = this.inputEl.closest('header-search');
    if (dialogEl) {
      // Show initial content (wildcard) when the dialog opens
      dialogEl.addEventListener('dialog:after-show', function () {
        if (!this.inputEl.value.trim()) {
          this._fetch('*');
        }
      }.bind(this));

      // Clear results and reset input when the dialog closes
      dialogEl.addEventListener('dialog:after-hide', function () {
        this.resultsEl.innerHTML = '';
        this.inputEl.value = '';
        this._lastSuccessfulSearchQuery = null;
        clearTimeout(this._debounceTimer);
      }.bind(this));
    }
  };

  /**
   * Suggested-products state uses HR query "*" with an empty input. Submitting the form
   * would otherwise navigate to /search with no q; inject "*" so the full search page matches.
   */
  HelloRetailSearchOverlay.prototype._onPredictiveFormSubmit = function () {
    if (!this.inputEl.value.trim() && this._lastSuccessfulSearchQuery === '*') {
      this.inputEl.value = '*';
    }
  };

  HelloRetailSearchOverlay.prototype._onInput = function () {
    clearTimeout(this._debounceTimer);
    var query = this.inputEl.value.trim();
    // Empty input → fall back to initial content
    if (!query) {
      this._debounceTimer = setTimeout(this._fetch.bind(this, '*'), 280);
      return;
    }
    this._debounceTimer = setTimeout(this._fetch.bind(this, query), 280);
  };

  HelloRetailSearchOverlay.prototype._fetch = function (query) {
    var self = this;
    this._recomGen++; // Invalidate any pending recom response from the previous query
    this.api.search(query).then(function (data) {
      self._render(data, query);
    }).catch(function (e) {
      console.error('[HR Search] Error:', e.message);
    });
  };

  HelloRetailSearchOverlay.prototype._render = function (data, query) {
    var H        = window.HelloRetailHelpers;
    var products = (data && data.result) ? data.result : [];

    if (!products.length) {
      this._lastSuccessfulSearchQuery = null;
      // No message for wildcard (initial content) — just leave the panel empty
      if (query !== '*') {
        this.resultsEl.innerHTML =
          '<p class="predictive-search__no-results text-lg">No results for &ldquo;' +
          H.escapeHtml(query) + '&rdquo;</p>';
        this._fetchRecoms(query, false);
      }
      return;
    }

    var grid = H.cloneTemplate('hr-search-results-tpl');
    if (!grid) return;

    // Update label: "Suggested products" for wildcard, "Products (N)" for real queries
    if (query !== '*') {
      var tpl        = document.getElementById('hr-search-results-tpl');
      var labelEl    = grid.querySelector('[data-hr-slot="resultsLabel"]');
      var totalCount = (data && data.product_results) || products.length;
      if (labelEl) {
        labelEl.textContent = ((tpl && tpl.dataset.productsLabel) || 'Products') + ' (' + totalCount + ')';
      }

      // Show "View all" buttons only when there are more results than displayed
      if (totalCount > 6) {
        grid.querySelectorAll('.hr-view-all').forEach(function (btn) {
          btn.removeAttribute('hidden');
        });
      }
    }

    var self           = this;
    var cardsContainer = grid.querySelector('[data-hr-slot="cardsContainer"]');
    products.forEach(function (product) {
      var card = self._renderProductCard(product);
      if (card) cardsContainer.appendChild(card);
    });

    this.resultsEl.innerHTML = '';
    this.resultsEl.appendChild(grid);
    this._lastSuccessfulSearchQuery = query;

    // Show recommendations below results for real queries only
    if (query !== '*') {
      this._fetchRecoms(query, true);
    }
  };

  HelloRetailSearchOverlay.prototype._renderProductCard = function (product) {
    var H    = window.HelloRetailHelpers;
    var card = H.cloneTemplate('hr-product-card-tpl');
    if (!card) return null;
    H.fillSlots(card, H.CARD_SLOT_FILLERS, product);
    return card;
  };

  /**
   * Fetch recommendations after a real search.
   * Uses the related key when the search returned products,
   * or the popular key when there were no results.
   */
  HelloRetailSearchOverlay.prototype._fetchRecoms = function (query, hasResults) {
    var settings = window.themeVariables && window.themeVariables.settings;
    var key      = hasResults
      ? (settings && settings.hrSearchRelatedKey)
      : (settings && settings.hrSearchPopularKey);

    if (!key) return; // Key not configured — silently skip

    var self = this;
    var gen  = this._recomGen; // Capture current generation

    this.api.loadRecom(key).then(function (recom) {
      // Discard if a newer search has started
      if (self._recomGen !== gen) return;

      var products = recom && recom.products;
      if (!products || !products.length) return;

      var tpl   = document.getElementById('hr-search-recoms-tpl');
      var label = hasResults
        ? (tpl && tpl.dataset.youMightLike)    || 'You might also like'
        : (tpl && tpl.dataset.popularProducts) || 'Popular products';

      self._renderRecoms(products, label);
    }).catch(function (e) {
      console.error('[HR Search] Recom error:', e.message);
    });
  };

  /**
   * Clone the recoms template, populate cards, and append to resultsEl.
   */
  HelloRetailSearchOverlay.prototype._renderRecoms = function (products, label) {
    var H       = window.HelloRetailHelpers;
    var section = H.cloneTemplate('hr-search-recoms-tpl');
    if (!section) return;

    var labelEl    = section.querySelector('[data-hr-slot="recomsLabel"]');
    var container  = section.querySelector('[data-hr-slot="recomsContainer"]');
    if (labelEl) labelEl.textContent = label;

    var self = this;
    products.forEach(function (product) {
      var card = self._renderProductCard(product);
      if (card) container.appendChild(card);
    });

    this.resultsEl.appendChild(section);
  };

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  document.addEventListener('DOMContentLoaded', function () {
    if (!window.HelloRetailAPI || !window.HelloRetailHelpers) {
      console.warn('[HR Search] hello-retail-helpers.js and hello-retail-api.js must load before hello-retail-search.js');
      return;
    }
    new HelloRetailSearchOverlay().init();
  });

  window.HelloRetailSearchOverlay = HelloRetailSearchOverlay;
})();
